/**
 * Состояние встреч (DEV-1947, экран «Встречи» — DEV-1973).
 *
 * Стор, а не состояние экрана плана: приглашение приходит человеку, где бы он
 * ни был, и баннер о нём висит в общем каркасе. Экран плана и баннер смотрят в
 * одно и то же состояние — иначе «Присоединиться» в шапке и мит-режим внизу
 * расходились бы в том, идёт ли встреча.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useAuthStore } from '@/features/auth/store'

import {
  endMeeting,
  getMeeting,
  getMeetingState,
  inviteToMeeting,
  joinMeeting,
  listMeetings,
  streamMeeting,
  type MeetingInvite,
  type MeetingSession,
} from './api'
import { MeetingPresence, NoteDoc, type PeerState } from './collab'
import { useNotesStore } from './notesStore'

/**
 * Паузы перед повторной подпиской, миллисекунды.
 *
 * Первая короткая — обрыв чаще всего мгновенный (выкат сервиса, моргнувший
 * wifi), и ждать после него пять секунд значит показывать человеку мёртвый
 * экран на ровном месте. Дальше пауза растёт: если сервис лежит, долбить его
 * каждую секунду с каждой вкладки бессмысленно.
 */
const RECONNECT_DELAYS = [1_000, 2_000, 5_000]

export const useMeetingStore = defineStore('meetings', () => {
  const auth = useAuthStore()

  const session = ref<MeetingSession | null>(null)
  const invites = ref<MeetingInvite[]>([])
  const error = ref<unknown>(null)

  /**
   * Человек нажал «Присоединиться» в баннере: плану остаётся открыть мит-режим,
   * когда он до него доедет. Через стор, а не через параметр адреса — режим не
   * состояние страницы, а способ её смотреть.
   */
  const pendingOpen = ref(false)

  /**
   * Связь с встречей потеряна — показываем это, только когда попытка
   * подключиться уже провалилась.
   *
   * Не «поток сейчас не открыт»: между попытками и в первую миллисекунду
   * подключения он тоже не открыт, и красная надпись на ровном месте пугала бы
   * человека там, где всё в порядке.
   */
  const isOffline = ref(false)

  let controller: AbortController | null = null

  const isActive = computed(() => !!session.value)
  const isAuthor = computed(() => session.value?.iAmAuthor ?? false)

  /** Кто на встрече сейчас — позванные, но не пришедшие, в счёт не идут */
  const participants = computed(() =>
    (session.value?.participants ?? []).filter((participant) => participant.joined),
  )

  /** Все, кого касается встреча: сначала пришедшие, следом ещё ожидаемые */
  const everyone = computed(() => [
    ...participants.value,
    ...(session.value?.participants ?? []).filter((participant) => !participant.joined),
  ])

  /**
   * История встреч — левая колонка экрана (DEV-1971).
   *
   * Отдельно от `session`: та отвечает на «где я сейчас», а список — на «где я
   * был». Идущая встреча есть и там и там, и это одна и та же встреча.
   */
  const meetings = ref<MeetingSession[]>([])
  const isListLoading = ref(false)
  const isListLoaded = ref(false)

  const loadMeetings = async () => {
    isListLoading.value = true

    try {
      meetings.value = await listMeetings()
      isListLoaded.value = true
      error.value = null
    } catch (failure) {
      error.value = failure
    } finally {
      isListLoading.value = false
    }
  }

  /**
   * Живые документы открытых заметок и присутствие соседей (DEV-1974).
   *
   * В сторе, а не в компоненте: правки приезжают потоком встречи, который
   * держит он же, и раскладывать их по карточкам больше некому. Сами документы
   * реактивными не делаются — Yjs держит своё состояние сам, а экран будит
   * счётчик правок.
   */
  const docs = new Map<string, NoteDoc>()
  const revision = ref(0)
  const peers = ref<PeerState[]>([])

  let presence: MeetingPresence | null = null

  const touch = () => {
    revision.value += 1
    peers.value = presence?.peers() ?? []
  }

  /** Открыть заметку на совместную правку */
  const openNote = async (noteId: string, fallbackBody: string): Promise<NoteDoc> => {
    const known = docs.get(noteId)

    if (known) return known

    const doc = new NoteDoc(noteId, touch)

    docs.set(noteId, doc)
    await doc.open(fallbackBody)
    touch()

    return doc
  }

  /** Закрыть заметку: закрепить состояние и отпустить документ */
  const closeNote = async (noteId: string): Promise<void> => {
    const doc = docs.get(noteId)

    if (!doc) return

    docs.delete(noteId)
    await doc.persist()
    doc.destroy()
    touch()
  }

  /** Сказать соседям, где мы и что делаем */
  const setPresence = (state: { noteId?: string; from?: number; to?: number; typing?: boolean }) => {
    presence?.setLocal(state)
  }

  /**
   * Встреча по идентификатору — из уже загруженного списка, а если её там нет,
   * то с сервера.
   *
   * Второе бывает у ссылки из уведомления: человек открывает встречу до того,
   * как список успел приехать, и отправлять его «куда-то в список» вместо
   * позвавшего разговора значит потерять то, ради чего он кликнул.
   */
  const meetingById = async (id: string): Promise<MeetingSession | null> => {
    const known = meetings.value.find((meeting) => meeting.id === id)

    if (known) return known

    try {
      const meeting = await getMeeting(id)

      // Список мог приехать, пока мы её спрашивали: экран открывают и
      // маршрутом, и переходом из баннера, и оба пути ведут сюда.
      meetings.value = [meeting, ...meetings.value.filter((item) => item.id !== meeting.id)]
      error.value = null

      return meeting
    } catch (failure) {
      error.value = failure

      return null
    }
  }

  const refreshState = async () => {
    try {
      const state = await getMeetingState()

      session.value = state.session
      invites.value = state.invites
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  const invite = async (userIds: string[]) => {
    if (!userIds.length) return

    try {
      const state = await inviteToMeeting(userIds)

      session.value = state.session
      invites.value = state.invites
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  const join = async (sessionId: string) => {
    try {
      const state = await joinMeeting(sessionId)

      session.value = state.session
      invites.value = state.invites
      pendingOpen.value = true
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  /** Завершить встречу. Всегда ту, что открыта сейчас, — её id, а не «текущую» */
  const end = async () => {
    const current = session.value

    if (!current) return

    try {
      await endMeeting(current.id)
      // Перечитываем, а не гасим руками: у человека могла остаться другая
      // встреча, и обнулять состояние вслепую значит спрятать её от него.
      await refreshState()
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  /**
   * Держит поток событий встречи, переподключаясь после обрывов.
   *
   * После каждого переподключения лента и участники перечитываются целиком:
   * события, прошедшие мимо во время обрыва, не восстанавливаются, и без
   * догона экран остался бы со вчерашним протоколом, выглядя живым.
   */
  const openStream = (sessionId: string) => {
    closeStream()

    const notes = useNotesStore()
    const own = new AbortController()

    controller = own
    isOffline.value = false

    presence = new MeetingPresence(
      sessionId,
      { userId: auth.user?.id ?? '', name: auth.user?.name ?? '' },
      touch,
    )
    touch()

    void (async () => {
      let attempt = 0

      while (!own.signal.aborted) {
        try {
          await streamMeeting(sessionId, {
            signal: own.signal,
            onEvent: (event) => {
              if (event.kind === 'note') void notes.load()
              if (event.kind === 'participants') void refreshState()
              // Встречу завершили — состояние перечитывается, и экран уйдёт за
              // ней сам: сессии больше нет.
              if (event.kind === 'ended') void refreshState()

              // Свои правки применены на месте: возвращать их себе же значит
              // гонять текст по кругу.
              if (event.actorId && event.actorId === auth.user?.id) return

              if (event.kind === 'crdt' && event.noteId && event.payload) {
                docs.get(event.noteId)?.applyRemote(event.payload, event.seq ?? 0)
              }

              // Правка не поместилась в потолок NOTIFY — тела у события нет,
              // и клиент дочитывает журнал сам.
              if (event.kind === 'crdt-gap' && event.noteId) {
                void docs.get(event.noteId)?.catchUp()
              }

              if (event.kind === 'awareness' && event.payload) {
                presence?.applyRemote(event.payload)
              }
            },
          })

          // Поток закончился без ошибки — так бывает при завершении встречи.
          attempt = 0
          isOffline.value = false
        } catch {
          // Отмена — это наш собственный уход, а не потеря связи: зажигать по
          // ней красную надпись значит пугать человека своим же действием.
          if (own.signal.aborted) return

          attempt += 1
          isOffline.value = true

          // Пока связи нет, чужие каретки не двигаются: оставить их значит
          // показывать, что сосед замер на середине слова, — а это мы его не
          // слышим.
          presence?.forgetPeers()
        }

        if (own.signal.aborted) return

        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)] ?? 5_000

        await new Promise((resolve) => setTimeout(resolve, delay))

        if (own.signal.aborted) return

        // Догоняем пропущенное до того, как снова слушать.
        await refreshState()

        if (!session.value) return

        void notes.load()

        // Правки, прошедшие мимо во время обрыва, догоняются журналом: у
        // каждой заметки свои номера, и по разрыву в них видно пропущенное.
        for (const doc of docs.values()) void doc.catchUp()

        isOffline.value = false
      }
    })()
  }

  const closeStream = () => {
    controller?.abort()
    controller = null
    isOffline.value = false

    for (const noteId of [...docs.keys()]) void closeNote(noteId)

    presence?.destroy()
    presence = null
    touch()
  }

  const consumePendingOpen = () => {
    const wanted = pendingOpen.value
    pendingOpen.value = false

    return wanted
  }

  return {
    session,
    invites,
    meetings,
    isListLoading,
    isListLoaded,
    loadMeetings,
    meetingById,
    revision,
    peers,
    openNote,
    closeNote,
    setPresence,
    error,
    isActive,
    isAuthor,
    isOffline,
    participants,
    everyone,
    pendingOpen,
    refreshState,
    invite,
    join,
    end,
    openStream,
    closeStream,
    consumePendingOpen,
  }
})
